import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';

import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';

import SettingsSwitch from '../../../../components/SettingsSwitch';

import { activateAuthLayout } from '../../../../store/actions';

import env from '../../../../env';

const DATA = gql`
  query getData {
    Settings(Type: "Admin Setting") {
      SettingId
      Value
      Type
    }
  }
`;

const CREATE_SETTINGS = gql`
  mutation createSettings($SettingsInput: SettingsInput) {
    createSettings(input: $SettingsInput) {
      Value
      Type
    }
  }
`;

const UPDATE_SETTINGS = gql`
  mutation updateSettings($SettingsInput: SettingsInput) {
    updateSettings(input: $SettingsInput) {
      Value
    }
  }
`;

const AdminSettings = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const { data } = useQuery(DATA);
  const [adminSettings, setAdminSettings] = useState(null);
  const [createSettings] = useMutation(CREATE_SETTINGS);
  const [updateSettings] = useMutation(UPDATE_SETTINGS);
  const [formResult, setFormResult] = useState({});
  const [isAdminSetPrev, setIsAdminSetPrev] = useState(false);

  useEffect(() => {
    if (data) {
      const { Settings = null } = data || {};
      if (Settings && adminSettings === null) {
        const value = JSON.parse(Settings.Value);
        setIsAdminSetPrev(true);
        setAdminSettings(value);
      }
    }
  }, [data, adminSettings]);

  const settingsDetailsChange = (e, field) => {
    const value = e.target ? e.target.value : e.value;
    const settings = Object.assign({}, adminSettings);
    settings[field] = value;
    setAdminSettings(settings);
  };

  const settingsDetailsSwitchChange = (e, field) => {
    const { checked = false } = e.target || {};
    const settings = Object.assign({}, adminSettings);
    settings[field] = checked;
    setAdminSettings(settings);
  };

  const saveSettings = () => {
    const settings = JSON.stringify(adminSettings);
    createSettings({
      variables: {
        SettingsInput: { Value: settings, Type: 'Admin Setting' },
      },
    })
      .then((result) => {
        const { createSettings = null } = (result && result.data) || {};
        if (createSettings) {
          const form = Object.assign({}, formResult);
          form['success'] = true;
          form['message'] = 'Custom Settings is saved successfully!';
          setFormResult(form);
          setIsAdminSetPrev(true);
        }
      })
      .catch((e) => {
        console.log({ e });
      });
  };

  const updateAndSaveSettings = () => {
    const settings = JSON.stringify(adminSettings);
    updateSettings({
      variables: {
        SettingsInput: { Value: settings, Type: 'Admin Setting' },
      },
    })
      .then((result) => {
        const { updateSettings = null } = (result && result.data) || {};
        if (updateSettings) {
          const form = Object.assign({}, formResult);
          form['success'] = true;
          form['message'] = 'Custom Settings is updated successfully!';
          setFormResult(form);
        }
      })
      .catch((e) => {
        console.log({ e });
      });
  };

  const sweetAlert = () => {
    if (formResult && (formResult.success || formResult.error)) {
      return (
        <React.Fragment>
          {formResult && formResult.success && (
            <SweetAlert
              success={formResult.success}
              error={formResult.error}
              title="Success"
              onConfirm={(e) => setFormResult({})}
              showConfirm={false}
              timeout={2500}
            >
              {formResult.message}
            </SweetAlert>
          )}
        </React.Fragment>
      );
    }

    return null;
  };

  return (
    <React.Fragment>
      {sweetAlert()}
      {data && (
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row>
                <Col>
                  <h4 className="page-title">Settings</h4>
                </Col>
              </Row>
            </div>
            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <Form>
                      <FormGroup>
                        <Row>
                          <Col>
                            <Label for="minimum_quote_margin">
                              Minimum Quote Margin (%)
                            </Label>
                          </Col>
                          <Col>
                            <Input
                              value={
                                adminSettings
                                  ? (adminSettings.MINIMUM_QUOTE_MARGIN || '')
                                  : ''
                              }
                              type="number"
                              name="minimum_quote_margin"
                              onChange={(e) =>
                                settingsDetailsChange(e, 'MINIMUM_QUOTE_MARGIN')
                              }
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col>
                            <Label for="target_quote_margin">
                              Target Quote Margin (%)
                            </Label>
                          </Col>
                          <Col>
                            <Input
                              value={
                                adminSettings
                                  ? (adminSettings.TARGET_QUOTE_MARGIN || '')
                                  : ''
                              }
                              type="number"
                              name="target_quote_margin"
                              onChange={(e) =>
                                settingsDetailsChange(e, 'TARGET_QUOTE_MARGIN')
                              }
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col>
                            <Label for="xero_sync_report_email">
                              Xero Sync Report Email
                            </Label>
                          </Col>
                          <Col>
                            <Input
                              value={
                                adminSettings
                                  ? (adminSettings.XERO_SYNC_REPORT_EMAIL || '')
                                  : ''
                              }
                              type="email"
                              name="xero_sync_report_email"
                              onChange={(e) =>
                                settingsDetailsChange(
                                  e,
                                  'XERO_SYNC_REPORT_EMAIL'
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col>
                            <Label for="xero_pull_enabled">
                              Pull product data from Xero
                            </Label>
                          </Col>
                          <Col>
                            <SettingsSwitch
                              checked={
                                adminSettings &&
                                adminSettings.XERO_PULL_ENABLED
                                  ? adminSettings.XERO_PULL_ENABLED
                                  : false
                              }
                              onChange={(e) =>
                                settingsDetailsSwitchChange(
                                  e,
                                  'XERO_PULL_ENABLED'
                                )
                              }
                              name="xero_pull_enabled"
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col>
                            <Label for="xero_push_enabled">
                              Push product data to Xero
                            </Label>
                          </Col>
                          <Col>
                            <SettingsSwitch
                              checked={
                                adminSettings &&
                                adminSettings.XERO_PUSH_ENABLED
                                  ? adminSettings.XERO_PUSH_ENABLED
                                  : false
                              }
                              onChange={(e) =>
                                settingsDetailsSwitchChange(
                                  e,
                                  'XERO_PUSH_ENABLED'
                                )
                              }
                              name="xero_push_enabled"
                            />
                          </Col>
                        </Row>
                      </FormGroup>
                      <FormGroup>
                        <Row>
                          <Col>
                            {isAdminSetPrev && (
                              <Button
                                color="primary"
                                onClick={updateAndSaveSettings}
                              >
                                Update Settings
                              </Button>
                            )}
                            {!isAdminSetPrev && (
                              <Button color="primary" onClick={saveSettings}>
                                Save Settings
                              </Button>
                            )}
                          </Col>
                          <Col>
                            <Button
                              color="info"
                              href={env.XERO_REDIRECT_URI}
                            >
                              Authenticate Xero Account
                            </Button>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(AdminSettings);
